// Full Row Detail: fixed-value columns ("hard-coded" columns).
//
// Each entry in section.detailConstantColumns adds one column whose value is the same on every
// row - text, a number or a date - after the categorization and calculated columns, aliased
// "detail:const:<id>". The Excel layout binds to that alias like any other detail column, so
// header, order, width and format are set on the Excel Layout tab.
//
// The value is validated here and, on the server, bound as a typed parameter
// (ReportSqlBuilder.ConstantColumns.cs): text NVARCHAR(4000), number DECIMAL(38, 6), date DATE.
// The SQL preview shows it as a literal for readability. A blank value gives a NULL column.
// Mirrors ReportSqlBuilder.ConstantColumns.cs - keep the two in step.
import type { DetailConstantColumn, DetailConstantValueType, MetricSection } from "../types";
import { sqlLiteral } from "./sqlExpressions";

export const DETAIL_CONST_ALIAS_PREFIX = "detail:const:";
export const MAX_DETAIL_CONSTANT_COLUMNS = 20;
export const DETAIL_CONSTANT_NAME_MAX_LENGTH = 128;
export const DETAIL_CONSTANT_TEXT_MAX_LENGTH = 4000;

const NUMBER_PATTERN = /^-?\d{1,18}(\.\d{1,6})?$/;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export const DETAIL_CONSTANT_TYPES: { key: DetailConstantValueType; label: string }[] = [
    { key: "text", label: "Text" },
    { key: "number", label: "Number" },
    { key: "date", label: "Date" },
];

export function detailConstantColumns(section: MetricSection): DetailConstantColumn[] {
    return section.displayAs === "detail" ? section.detailConstantColumns || [] : [];
}

export function detailConstAlias(id: string) {
    return `${DETAIL_CONST_ALIAS_PREFIX}${id}`;
}

export function isDetailConstAlias(alias: string) {
    return alias.startsWith(DETAIL_CONST_ALIAS_PREFIX);
}

function isValidDate(value: string) {
    const match = DATE_PATTERN.exec(value);
    if (!match) return false;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return date.getUTCFullYear() === Number(match[1]) && date.getUTCMonth() === Number(match[2]) - 1 && date.getUTCDate() === Number(match[3]);
}

/** Why one fixed-value column can't be used, or null. */
export function detailConstantIssue(section: MetricSection, column: DetailConstantColumn): string | null {
    const name = (column.name || "").trim();
    if (!name) return "Give the fixed value column a name.";
    if (name.length > DETAIL_CONSTANT_NAME_MAX_LENGTH) return `"${name}": the column name must be ${DETAIL_CONSTANT_NAME_MAX_LENGTH} characters or fewer.`;
    const sameName = detailConstantColumns(section).filter((other) => (other.name || "").trim().toLowerCase() === name.toLowerCase());
    if (sameName.length > 1) return `More than one fixed value column is named "${name}".`;
    const value = (column.value ?? "").trim();
    if (!value) return null; // blank = empty column
    if (column.valueType === "number" && !NUMBER_PATTERN.test(value)) return `"${name}": enter a number (up to 18 digits and 6 decimals).`;
    if (column.valueType === "date" && !isValidDate(value)) return `"${name}": enter a valid date.`;
    if (column.valueType === "text" && value.length > DETAIL_CONSTANT_TEXT_MAX_LENGTH) return `"${name}": the value must be ${DETAIL_CONSTANT_TEXT_MAX_LENGTH} characters or fewer.`;
    return null;
}

export function detailConstantIssues(section: MetricSection) {
    const issues = detailConstantColumns(section).map((column) => detailConstantIssue(section, column)).filter((issue): issue is string => !!issue);
    if (detailConstantColumns(section).length > MAX_DETAIL_CONSTANT_COLUMNS) issues.push(`A metric can have at most ${MAX_DETAIL_CONSTANT_COLUMNS} fixed value columns.`);
    return Array.from(new Set(issues));
}

/** The preview SQL for one column's value, or null when it is not valid. */
export function detailConstantSql(column: DetailConstantColumn): string | null {
    const value = (column.value ?? "").trim();
    if (column.valueType === "number") {
        if (!value) return "CAST(NULL AS DECIMAL(38, 6))";
        return NUMBER_PATTERN.test(value) ? `CAST(${value} AS DECIMAL(38, 6))` : null;
    }
    if (column.valueType === "date") {
        if (!value) return "CAST(NULL AS DATE)";
        return isValidDate(value) ? `CAST(${sqlLiteral(value)} AS DATE)` : null;
    }
    if (!value) return "CAST(NULL AS NVARCHAR(4000))";
    return value.length <= DETAIL_CONSTANT_TEXT_MAX_LENGTH ? `CAST(N${sqlLiteral(value)} AS NVARCHAR(4000))` : null;
}

/** e.g. 'Program = "NEMT"', 'Rate = 12.50', 'Start = 2026-01-31', 'Notes = (blank)'. */
export function detailConstantText(column: DetailConstantColumn) {
    const value = (column.value ?? "").trim();
    const shown = !value ? "(blank)" : column.valueType === "text" ? `"${value}"` : value;
    return `${(column.name || "").trim() || "Untitled"} = ${shown}`;
}

/** Default Excel format for the column's type. */
export function detailConstantDefaultFormat(valueType: DetailConstantValueType) {
    return valueType === "number" ? "decimal2" as const : valueType === "date" ? "MM-dd-yyyy" as const : "text" as const;
}
